/**
 * Checks the Create Account phone number before it goes to Cognito.
 *
 * `phoneNumber` is what Amplify's PhoneField hands over: the dial code glued to
 * whatever was typed, with dashes and parentheses already removed. Spaces and
 * dots are NOT removed by Amplify, and Cognito answers "311 555 1212" with a
 * bare "Invalid phone number format", so they are stripped here for every
 * country.
 *
 * Only +1 numbers get length rules. +1 is the North American Numbering Plan
 * (the US, Canada, and much of the Caribbean): always a 3-digit area code plus
 * a 7-digit number, and neither the area code nor the next three digits can
 * start with 0 or 1. No other dial code in Amplify's list starts with "+1", so
 * the prefix identifies it. Other countries' lengths vary too much to check
 * without a full phone-number library, so Cognito's own check covers them.
 */
export interface PhoneNumberCheck {
  phoneNumber: string;
  error?: string;
}

export function checkPhoneNumber(phoneNumber: string): PhoneNumberCheck {
  const cleaned = phoneNumber.replace(/[\s.]/g, '');
  if (!cleaned.startsWith('+1')) {
    return { phoneNumber: cleaned };
  }

  let digits = cleaned.slice(2);
  if (/\D/.test(digits)) {
    return {
      phoneNumber: cleaned,
      error: 'Phone Number: please use numbers only.',
    };
  }
  // The usual reason a US number runs to 11 digits is a leading 1, which is the
  // country code the +1 box already supplies.
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length !== 10) {
    return {
      phoneNumber: cleaned,
      error: `Phone Number: a +1 number needs 10 digits, the area code plus the number. You entered ${digits.length}.`,
    };
  }
  if (/[01]/.test(digits[0])) {
    return {
      phoneNumber: cleaned,
      error: 'Phone Number: the area code cannot start with 0 or 1.',
    };
  }
  if (/[01]/.test(digits[3])) {
    return {
      phoneNumber: cleaned,
      error:
        'Phone Number: the three digits after the area code cannot start with 0 or 1.',
    };
  }
  return { phoneNumber: `+1${digits}` };
}
